import {
  FormControl,
  HStack,
  Input,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useFormik } from "formik";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { ColorModeSwitcher } from "../../ColorModeSwitcher";
import { useBeforeUnload } from "../../lib/useBeforeUnload";
import useCustomToast from "../../lib/useCustomToast";
import { useHaveEmail } from "../../lib/useHaveEmail";
import { ButtonPrimary } from "../button/ButtonPrimary";

interface Props extends StackProps {
  url: string;
}

const initialValues = {
  field1: "",
  field2: "",
  field3: "",
  field4: "",
  field5: "",
  field6: "",
};

export const RegisterOtpForm = ({ url, ...props }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();
  const navigate = useNavigate();
  useHaveEmail();
  useBeforeUnload();

  const field1Ref = useRef<HTMLInputElement>(null);
  const field2Ref = useRef<HTMLInputElement>(null);
  const field3Ref = useRef<HTMLInputElement>(null);
  const field4Ref = useRef<HTMLInputElement>(null);
  const field5Ref = useRef<HTMLInputElement>(null);
  const field6Ref = useRef<HTMLInputElement>(null);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      field1: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
      field2: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
      field3: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
      field4: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
      field5: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
      field6: yup
        .number()
        .required("Harus diisi")
        .typeError("Harus berupa angka"),
    }),
    onSubmit: (values) => {
      setLoading(true);

      const combinedValue = `${values.field1}${values.field2}${values.field3}${values.field4}${values.field5}${values.field6}`;
      const otpValue = combinedValue;
      const registeredEmail = localStorage.getItem("registeredEmail");

      const request = {
        email: registeredEmail,
        otp: otpValue,
      };

      axios
        .post(
          `${process.env.REACT_APP_WEB_API_BASE_URL}/v1/auth/buyer/verification-otp`,
          request
        )
        .then((res) => {
          toast({
            title: "Verifikasi OTP sukses, set password untuk akun anda",
            status: "success",
          });
          navigate("?step=password");
        })
        .catch((err) => {
          toast({
            title: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    nextFieldRef: React.RefObject<HTMLInputElement> | null,
    prevFieldRef: React.RefObject<HTMLInputElement> | null
  ) => {
    formik.handleChange(e);

    if (e.target.value.length === 1 && nextFieldRef?.current) {
      nextFieldRef.current.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    if (e.key === "Backspace") {
      const currentField =
        formik.values[`field${currentIndex + 1}` as keyof typeof formik.values];
      if (currentField === "" && currentIndex > 0) {
        formik.setFieldValue(
          `field${currentIndex + 1}` as keyof typeof formik.values,
          ""
        );
        formik.setFieldValue(
          `field${currentIndex}` as keyof typeof formik.values,
          ""
        );

        const prevFieldRef = [
          field1Ref,
          field2Ref,
          field3Ref,
          field4Ref,
          field5Ref,
          field6Ref,
        ][currentIndex - 1];
        if (prevFieldRef?.current) {
          prevFieldRef.current.focus();
        }
      }
    }
  };

  return (
    <VStack p={10} {...props}>
      <Text
        fontWeight={"semibold"}
        fontSize={[18, null, 20]}
        textAlign={"center"}
      >
        Verifikasi Kode OTP
      </Text>

      <Text
        variant={"secondary"}
        textAlign={"center"}
        fontSize={[12, null, 14]}
      >
        Kami telah mengirimkan kode OTP ke email anda, periksa folder kotak
        masuk/spam
      </Text>

      <form
        id="registerOtpForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%", marginTop: "40px" }}
      >
        <VStack gap={6}>
          <HStack flexShrink={0}>
            <FormControl
              isInvalid={
                formik.errors.field1 && formik.touched.field1 ? true : false
              }
            >
              <Input
                ref={field1Ref}
                name="field1"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field1 || ""}
                onChange={(e) => handleChange(e, field2Ref, null)}
                onKeyDown={(e) => handleKeyDown(e, 0)}
              />
            </FormControl>

            <FormControl
              isInvalid={
                formik.errors.field2 && formik.touched.field2 ? true : false
              }
            >
              <Input
                ref={field2Ref}
                name="field2"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field2 || ""}
                onChange={(e) => handleChange(e, field3Ref, field1Ref)}
                onKeyDown={(e) => handleKeyDown(e, 1)}
              />
            </FormControl>

            <FormControl
              isInvalid={
                formik.errors.field3 && formik.touched.field3 ? true : false
              }
            >
              <Input
                ref={field3Ref}
                name="field3"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field3 || ""}
                onChange={(e) => handleChange(e, field4Ref, field2Ref)}
                onKeyDown={(e) => handleKeyDown(e, 2)}
              />
            </FormControl>

            <FormControl
              isInvalid={
                formik.errors.field4 && formik.touched.field4 ? true : false
              }
            >
              <Input
                ref={field4Ref}
                name="field4"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field4 || ""}
                onChange={(e) => handleChange(e, field5Ref, field3Ref)}
                onKeyDown={(e) => handleKeyDown(e, 3)}
              />
            </FormControl>

            <FormControl
              isInvalid={
                formik.errors.field5 && formik.touched.field5 ? true : false
              }
            >
              <Input
                ref={field5Ref}
                name="field5"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field5 || ""}
                onChange={(e) => handleChange(e, field6Ref, field4Ref)}
                onKeyDown={(e) => handleKeyDown(e, 4)}
              />
            </FormControl>

            <FormControl
              isInvalid={
                formik.errors.field6 && formik.touched.field6 ? true : false
              }
            >
              <Input
                ref={field6Ref}
                name="field6"
                p={0}
                size={"md"}
                maxW={"42px"}
                textAlign={"center"}
                maxLength={1}
                placeholder="*"
                value={formik.values.field6 || ""}
                onChange={(e) => handleChange(e, null, field5Ref)}
                onKeyDown={(e) => handleKeyDown(e, 5)}
              />
            </FormControl>
          </HStack>

          {(formik.errors.field1 && formik.touched.field1) ||
          (formik.errors.field2 && formik.touched.field2) ||
          (formik.errors.field3 && formik.touched.field3) ||
          (formik.errors.field4 && formik.touched.field4) ||
          (formik.errors.field5 && formik.touched.field5) ||
          (formik.errors.field6 && formik.touched.field6) ? (
            <Text fontSize={[12, null, 14]} color={"red.300"}>
              Semua field harus diisi
            </Text>
          ) : undefined}

          <ButtonPrimary
            form="registerOtpForm"
            isLoading={loading}
            loadingText="Loading"
            spinnerPlacement="start"
            type="submit"
            w={"100%"}
            maxW={"292px"}
          >
            Verifikasi
          </ButtonPrimary>

          <ColorModeSwitcher
            position={"absolute"}
            bottom={0}
            right={0}
            fontSize={"24px"}
            mb={4}
            mr={4}
          />
        </VStack>
      </form>
    </VStack>
  );
};
